import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Refresh } from 'components/Icons';

const ANIMATION_DURATION_MS = 800;

const ProgressLoading = ({ className, progress }) => {
  const [displayProgress, setDisplayProgress] = useState(progress);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const displayProgressRef = useRef(progress);
  displayProgressRef.current = displayProgress;

  useEffect(() => {
    const startValue = displayProgressRef.current;
    startTimeRef.current = null;

    const step = (timestamp) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const t = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      const nextValue = startValue + (progress - startValue) * t;

      setDisplayProgress(nextValue);

      if (t < 1) {
        animationRef.current = requestAnimationFrame(step);
      }
    };

    animationRef.current = requestAnimationFrame(step);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [progress]);

  const isComplete = Math.round(displayProgress) >= 100;

  const getMessage = () => {
    if (displayProgress <= 33) return 'Validating your submitted information';
    if (displayProgress <= 66) return 'Analyzing key eligibility factors';
    return 'Performing final review checks';
  };

  return (
    <div className={`flex flex-col gap-2 items-center text-center ${className}`}>
      <div className="w-12 h-12 my-4 text-blue-500 bg-blue-100 rounded-full flex items-center justify-center">
        <Refresh className="animate-spin" />
      </div>
      <h2 className="text-2xl font-semibold">Processing Your Application</h2>
      <span className="text-sm text-slate-500">This will take a moment</span>
      <div className="w-[450px] max-w-full mt-6 mb-4">
        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${displayProgress}%` }} />
        </div>
        <div className="flex justify-between mt-2 text-sm">
          <span className={`font-medium ${isComplete ? 'text-green-500' : 'text-slate-500'}`}>{Math.round(displayProgress)}% Complete</span>
          <span className={`font-medium ${isComplete ? 'text-green-500' : 'text-blue-500'}`}>{isComplete ? 'Done' : 'Processing...'}</span>
        </div>
      </div>
      <p className="text-xs text-slate-500">{getMessage()}</p>
    </div>
  );
};

ProgressLoading.propTypes = {
  className: PropTypes.node,
  progress: PropTypes.number,
};

ProgressLoading.defaultProps = {
  className: '',
  progress: 0,
}

export default ProgressLoading;
